import { Container, useToast } from "@/components";
import { PulseLoader } from "@/components/pulse_loader";
import { useConfig } from "@/core/config";
import PaymentElementInput, {
  PaymentElementInputActions,
} from "@/core/payment_element_input";
import {
  useAppleGooglePayEmbedInCheckoutPage,
  useApplePayFixFeatureFlag,
  usePaymentModalFeatureFlag,
} from "@/core/splitio";
import { StripeProviderV2 } from "@/core/stripe";
import { tranzilaIframeURL } from "@/core/tranzila";
import { MembershipDetail, PartnerOffering } from "@/data/partner_offering";
import { getTranzilaAdditionalParams } from "@/data/resource_integration";
import { usePrevious } from "@/hooks/use_previous";
import { buildPathWithParams } from "@/hooks/use_query_string";
import { useI18n } from "@/lib/i18n/i18n_provider";
import {
  useTrackMembershipPurchase,
  useTrackOrderCompletedEcommerceEvent,
} from "@/offering/analytics_ecommerce_events";
import {
  Order,
  OrderStatus,
  Quote,
  SpaceOffering,
  useOrderQueryCallback,
  usePartnerPaymentConfigsQuery,
  useTranzilaLocationPartnerIntegration,
} from "@/offering/api";
import { PartnerConfig, PaymentMethod } from "@/offering/partner_config";
import { Spacer } from "@/offering/spacer";
import { isEmpty } from "lodash";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Form, Modal, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { CheckoutFormInput } from "../hooks/use_payment";
import * as Sentry from "@sentry/browser";
import { useAnalytics } from "@/lib/analytics/analytics";
import { OrderParams } from "@/offering/order_params";
import { Space } from "@/data/space";
import { Location } from "@/data";
import { TemplateCheckoutForm } from "../templates/template_offering_checkout_base";
import { usePartnerDiscountPopupQuery } from "../hooks/use_partner_discount_popup_query";
import { hideDiscountPopupFBInLocalStorage } from "@/core/discount_popup_provider";
import { usePaymentTimer } from "../hooks/use_payment_timer";
import { ExpiredPaymentWarning } from "./expired_payment_warning";

interface PaymentsProps {
  partnerConfig: PartnerConfig;
  order?: Order;
  partnerOffering: PartnerOffering;
  form: TemplateCheckoutForm;
  quote?: Quote;
  orderParams: OrderParams;
  analyticsName?: string;
  space?: Space;
  location?: Location;
  spaceOffering?: SpaceOffering;
  onPaymentClose: () => void;
  paymentType: string;
  onChangePaymentType: (paymentType: string) => void;
  paymentProcessing: boolean;
  onPaymentProcessing: (value: boolean) => void;
  onErrorTextRef: (
    ref: HTMLElement | null,
    errorId: keyof CheckoutFormInput
  ) => void;
  loadingTranzila?: boolean;
  onLoadingTranzila?: (value: boolean) => void;
  errorElementRef: React.RefObject<HTMLDivElement>;
  membership?: MembershipDetail;
}

const STRIPE_POLLING_TIMEOUT_IN_SECOND = 60;

export function Payments({
  partnerConfig,
  order,
  partnerOffering,
  form,
  quote,
  orderParams,
  analyticsName,
  space,
  location,
  spaceOffering,
  onPaymentClose,
  paymentType,
  onChangePaymentType,
  onPaymentProcessing,
  paymentProcessing,
  onErrorTextRef,
  loadingTranzila,
  onLoadingTranzila,
  errorElementRef,
  membership,
}: PaymentsProps) {
  const { fbt, locale } = useI18n();
  const config = useConfig();
  const analytics = useAnalytics();

  const { values, errors, setFieldValue, touched } = form;

  const tranzilaLocationPartnerIntegration =
    useTranzilaLocationPartnerIntegration(space?.locationID);

  const toast = useToast();
  const getOrder = useOrderQueryCallback();
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);
  const trackMembershipPurchase = useTrackMembershipPurchase();

  const history = useHistory();
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerOffering.partner.id);
  const appleFixFeatureFlag = useApplePayFixFeatureFlag(
    partnerOffering.partner.id
  );
  const paymentModalFeatureFlag = usePaymentModalFeatureFlag();
  const [googleApplePayPaymentSucceeded, setGoogleApplePayPaymentSucceeded] =
    useState(false);
  const paymentElementRef = useRef<PaymentElementInputActions>();
  const completedRef = useRef(false);
  const redirectUrl = order
    ? `${window.location.origin}${buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      )}`
    : "";
  const [directingToConfirmPage, setDirectingToConfirmPage] = useState(false);

  const { isExpired, resetTimer } = usePaymentTimer(!!order);

  const prevOrder = usePrevious(order);
  const { data: discountPopupData } = usePartnerDiscountPopupQuery(
    partnerOffering.partner.id
  );

  const { data: paymentConfig } = usePartnerPaymentConfigsQuery(
    partnerOffering.partner.id,
    location?.id
  );

  useEffect(() => {
    if (
      isGoogleApplePayEmbedInCheckoutPage &&
      quote &&
      quote.totalPrice > 0 &&
      isEmpty(quote.paymentClientSecret)
    ) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          partnerId: partnerOffering.partner.id,
          offeringId: partnerOffering.id,
          offeringSlug: partnerOffering.slug,
        });
        Sentry.captureMessage(
          `WL: Cannot initialize payment element because the payment client secret is nil: ${partnerOffering?.name}, slug: ${partnerOffering?.slug}`
        );
      });
    }
  }, [quote, isGoogleApplePayEmbedInCheckoutPage, partnerOffering]);

  const handlePaymentPaid = useCallback(
    (order: Order) => {
      resetTimer();

      if (membership) {
        trackMembershipPurchase(order, membership, location, partnerOffering);
      } else {
        trackOrderCompletedEcommerceEvent(
          analyticsName,
          order,
          space,
          location,
          spaceOffering
        );
      }

      if (
        !!discountPopupData?.promotion.code &&
        discountPopupData?.promotion.code === order.promotionLine?.code
      ) {
        hideDiscountPopupFBInLocalStorage();
      }

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    },
    [
      analyticsName,
      space,
      location,
      partnerOffering,
      history,
      trackMembershipPurchase,
      trackOrderCompletedEcommerceEvent,
      spaceOffering,
      membership,
      discountPopupData?.promotion.code,
      resetTimer,
    ]
  );

  const handlePaymentError = useCallback(
    (errorMessage: string) => {
      const replay = Sentry.getReplay();
      replay?.start();
      // This is a duplicate event to above, but should make it easier to track errors on Mixpanel
      // Depending on usage, we may want to remove the above event
      analytics.event(
        "Checkout Payment Error",
        {
          "Error Message": errorMessage,
          Values: values,
          "Order Params": orderParams,
          "Payment Type": paymentType,
          "Order ID": order?.id,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
      toast.error({ message: errorMessage });
      onPaymentProcessing(false);
    },
    [
      analytics,
      toast,
      values,
      order,
      orderParams,
      onPaymentProcessing,
      paymentType,
    ]
  );

  const handleStartPollingOrder = useCallback(async () => {
    await new Promise<void>((resolve) => {
      let count = 0;

      // Start polling
      const interval = setInterval(async () => {
        // poll for maximum 60 seconds
        if (order && count < STRIPE_POLLING_TIMEOUT_IN_SECOND) {
          const polledOrder = await getOrder(order.id);
          count++;
          if (
            (polledOrder.status === OrderStatus.Paid ||
              polledOrder.status === OrderStatus.Confirmed) &&
            polledOrder.orderItems.length > 0
          ) {
            clearInterval(interval);
            // Even though we clear interval, async nature may still call this function
            // multiple times as response is returned
            if (!completedRef.current) {
              handlePaymentPaid(polledOrder);
              completedRef.current = true;
            }
            resolve();
          }
        } else {
          clearInterval(interval);
          handlePaymentError(fbt("Could not get order confirmation"));
          resolve();
        }
      }, 1000);
    });
  }, [order, getOrder, handlePaymentPaid, handlePaymentError, fbt]);

  useEffect(() => {
    // For usual card payment: we will create order first and then submit payment. once payment is successful, we will start polling order.
    // For apple/google pay: we will submit payment and create order at the same time. there can happen 2 things:
    // - order is created first and then payment is successful. in this case, we will start polling order.
    // - payment is successful first and then order is created. in this case, we will wait for order creation to trigger polling.
    // - user cancels payment. in this case, we will not start polling order. when they retry payment and is successful, we will start polling order.
    // in either case, we will start polling order only when payment is successful.
    if (
      (order !== prevOrder || (order && googleApplePayPaymentSucceeded)) &&
      isGoogleApplePayEmbedInCheckoutPage &&
      paymentElementRef.current
    ) {
      onPaymentProcessing(true);
      if (appleFixFeatureFlag) {
        if (
          !paymentType.includes("apple") &&
          !paymentType.includes("google_pay")
        ) {
          paymentElementRef.current.submitPayment();
        } else if (googleApplePayPaymentSucceeded) {
          handleStartPollingOrder();
        }
      } else {
        paymentElementRef.current.submitPayment();
      }
    }
  }, [
    order,
    prevOrder,
    isGoogleApplePayEmbedInCheckoutPage,
    appleFixFeatureFlag,
    handleStartPollingOrder,
    googleApplePayPaymentSucceeded,
    onPaymentProcessing,
    paymentType,
  ]);

  const handlePaymentSuccess = useCallback(async () => {
    analytics.event(
      "Checkout Payment Success",
      {
        "Quote ID": quote?.id,
        "Total Price": quote?.totalPrice,
        Currency: quote?.currency,
        "Payment Type": paymentType,
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
    if (appleFixFeatureFlag) {
      if (
        !paymentType.includes("apple") &&
        !paymentType.includes("google_pay")
      ) {
        await handleStartPollingOrder();
      } else {
        // trigger effect that triggers order polling once payment is successful and order is created
        setGoogleApplePayPaymentSucceeded(true);
      }
    } else {
      await handleStartPollingOrder();
    }
  }, [
    appleFixFeatureFlag,
    handleStartPollingOrder,
    analytics,
    quote,
    paymentType,
  ]);

  const handleIframeMessage = useCallback(
    async (e) => {
      if (e.data === "TRANZILA_PAYMENT_SUCCESS") {
        setDirectingToConfirmPage(true);
        await handlePaymentSuccess();
      }
      if (e.data === "TRANZILA_PAYMENT_FAILED") {
        onPaymentClose();
        toast.error({ message: "Payment failed." });
      }
    },
    [handlePaymentSuccess, onPaymentClose, toast]
  );

  useEffect(() => {
    window.addEventListener("message", handleIframeMessage);
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [handleIframeMessage]);

  const handlePaymentClose = useCallback(() => {
    resetTimer();
    onPaymentClose();
  }, [onPaymentClose, resetTimer]);

  if (partnerConfig.payment === PaymentMethod.Stripe) {
    return (
      <Fragment>
        {paymentModalFeatureFlag ? (
          <Modal
            show={!!order}
            centered
            size="lg"
            backdrop="static"
            onHide={() => {
              if (paymentProcessing) {
                return;
              }

              handlePaymentClose();
            }}
            className="header-center"
          >
            <Modal.Header closeButton={!isExpired}>
              <Modal.Title className="fw-bold">{fbt("Payment")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {isExpired && (
                <ExpiredPaymentWarning
                  orderId={order?.id}
                  paymentType={paymentType}
                />
              )}
              {order && !isExpired && (
                <StripeProviderV2
                  clientSecret={order.stripeClientSecret}
                  partnerSlug={partnerConfig.slug}
                  stripePublishableKey={paymentConfig?.stripePublishableKey}
                >
                  <PaymentElementInput
                    quote={quote}
                    redirectUrl={redirectUrl}
                    onSuccess={handlePaymentSuccess}
                    paymentProcessing={paymentProcessing}
                    onProcessing={() => onPaymentProcessing(true)}
                    onError={handlePaymentError}
                    billingInfo={values}
                    setFieldValue={setFieldValue}
                    onChangePaymentType={onChangePaymentType}
                    partnerId={partnerOffering.partner.id}
                    showConfirmButton
                    ref={paymentElementRef}
                    paymentSessionExpired={isExpired}
                  />
                </StripeProviderV2>
              )}
            </Modal.Body>
          </Modal>
        ) : (
          <>
            <h3>{fbt("Payment information")}</h3>
            {quote ? (
              quote.paymentClientSecret ? (
                <div ref={errorElementRef}>
                  <StripeProviderV2
                    stripePublishableKey={paymentConfig?.stripePublishableKey}
                    clientSecret={quote.paymentClientSecret}
                    partnerSlug={partnerConfig.slug}
                  >
                    <PaymentElementInput
                      quote={quote}
                      redirectUrl={redirectUrl}
                      onSuccess={handlePaymentSuccess}
                      paymentProcessing={paymentProcessing}
                      onProcessing={() => onPaymentProcessing(true)}
                      onError={handlePaymentError}
                      onChangePaymentType={onChangePaymentType}
                      billingInfo={values}
                      ref={paymentElementRef}
                      setFieldValue={setFieldValue}
                      partnerId={partnerOffering.partner.id}
                      paymentSessionExpired={isExpired}
                    />
                  </StripeProviderV2>
                  {errors.paymentComplete && touched.paymentComplete && (
                    <Form.Text
                      className="text-danger"
                      ref={(ref: HTMLElement | null) =>
                        onErrorTextRef(ref, "paymentComplete")
                      }
                    >
                      {errors.paymentComplete}
                    </Form.Text>
                  )}
                </div>
              ) : (
                <span>
                  {fbt(
                    "This crash has been reported to us and we will work on resolving the issue as quickly as possible."
                  )}
                </span>
              )
            ) : (
              <Container>
                <Row justifyContent="center">
                  <PulseLoader />
                </Row>
              </Container>
            )}
            <Spacer size={56} />
          </>
        )}
      </Fragment>
    );
  } else if (partnerConfig.payment === PaymentMethod.Tranzila) {
    return (
      <Modal
        show={!!order}
        onHide={handlePaymentClose}
        centered
        className="header-center"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">{fbt("Payment")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {
            <>
              {isExpired && (
                <ExpiredPaymentWarning
                  orderId={order?.id}
                  paymentType={paymentType}
                />
              )}
              {directingToConfirmPage && (
                <div className="p-3 text-center">
                  <div className="text-primary">
                    {fbt("Redirecting you to confirmation page")}
                  </div>
                  <Spacer size={20} />
                  <PulseLoader />
                </div>
              )}
              {order && partnerConfig.tranzilaId && !isExpired && (
                <div className="template-offering-checkout-tranzila-iframe-container">
                  {loadingTranzila && (
                    <div className="d-flex justify-content-center">
                      <PulseLoader />
                    </div>
                  )}
                  <iframe
                    onLoad={() => onLoadingTranzila?.(false)}
                    className="template-offering-checkout-tranzila-iframe"
                    title="Tranzila payment"
                    src={tranzilaIframeURL(
                      partnerConfig.tranzilaId,
                      order,
                      config.tranzilaNotifyURLAddress,
                      locale,
                      getTranzilaAdditionalParams(
                        tranzilaLocationPartnerIntegration
                      )
                    )}
                  />
                </div>
              )}
            </>
          }
        </Modal.Body>
      </Modal>
    );
  }

  return null;
}
